import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from '@/assets/icons/mail_gray_60.png'


const _withScopeId = n => (_pushScopeId("data-v-8b2dd406"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "member-manage" }
const _hoisted_2 = {
  key: 0,
  class: "alert alert-primary alert-sm"
}
const _hoisted_3 = { class: "share-profile" }
const _hoisted_4 = { class: "form-check form-switch" }
const _hoisted_5 = {
  for: "shareProfileOption",
  class: "form-check-label"
}
const _hoisted_6 = {
  key: 0,
  src: _imports_0,
  class: "invited"
}
const _hoisted_7 = ["checked", "disabled"]
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "help-text-sm" }, " Ensure other group members can see this person in their family tree. ", -1))
const _hoisted_9 = { key: 1 }
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", { class: "form-label" }, " Role ", -1))
const _hoisted_11 = { class: "member-role" }
const _hoisted_12 = {
  key: 0,
  src: _imports_0,
  class: "invited"
}
const _hoisted_13 = { class: "help-text-sm" }
const _hoisted_14 = {
  key: 2,
  class: "can-invite"
}
const _hoisted_15 = { class: "form-check form-switch" }
const _hoisted_16 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  for: "canInviteOption",
  class: "form-check-label"
}, " Can invite others ", -1))
const _hoisted_17 = ["disabled"]
const _hoisted_18 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "help-text-sm" }, " Allow the group member to share profiles with the group and invite other people to join. ", -1))

import { computed, nextTick, reactive, ref, watch } from 'vue'
import { PotentialMember, useFamilyGroupMemberManager } from './FamilyGroupMemberManager'
import { FamilyGroupMember, GroupMemberRole, ItemPermissions } from '@/gp/GroupAdminModel'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useInstantSave } from '@/util/InstantSave'
import { PatchChange } from '@/util/Api'
import { useTokenManager } from '@/auth/TokenManager'


export default /*#__PURE__*/_defineComponent({
  __name: 'FamilyGroupMemberManage',
  props: {
    familyGroupId: {},
    pm: {},
    showActions: { type: Boolean }
  },
  emits: ["shareProfile", "removeProfile", "removeUser"],
  setup(__props: any, { emit }) {

const props = __props;





const canInviteRoles = [
  GroupMemberRole.Owner,
  GroupMemberRole.Administrator,
  GroupMemberRole.Recruiter,
]

let ignoreChanges = false

const busyRef = ref(false)
const editMember = reactive(new FamilyGroupMember())

const familyGroupStore = useFamilyGroupStore()
const memberStore = useFamilyGroupMemberStore()
const memberManager = useFamilyGroupMemberManager()
const tokenManager = useTokenManager()

const memberRef = computed(() => props.pm.member)
const memDisplayRoleRef = computed<GroupMemberRole>({
  get: () => editMember.role == GroupMemberRole.Recruiter ? GroupMemberRole.Member : editMember.role,
  set: value => {
    if (value == GroupMemberRole.Member) {
      editMember.role = memCanInviteRef.value ? GroupMemberRole.Recruiter : GroupMemberRole.Member
    }
    else {
      editMember.role = value
    }
  }
})
const memCanInviteRef = computed({
  get: () => canInviteRoles.includes(editMember.role),
  set: value => {
    if (memDisplayRoleRef.value == GroupMemberRole.Member) {
      editMember.role = value ? GroupMemberRole.Recruiter : GroupMemberRole.Member
    }
  }
})

useInstantSave(memberRef, editMember, ['role'], saveMemberChangesAsync)

const memUserIsSelfRef = computed(() => props.pm.userId == tokenManager.current.userId)
const canEditUserRef = computed(() => memUserIsSelfRef.value || props.pm.isUserSupervisor)
const memIsGroupOwnerRef = computed(() => props.pm.member?.role == GroupMemberRole.Owner)

const groupPermissionsRef = computed(() => familyGroupStore.getLoadedPermissions(props.familyGroupId)) // loaded by parent
const canEditGroupRef = computed(() => groupPermissionsRef.value & ItemPermissions.Modify)
const canChangeRoleRef = computed(() => canEditGroupRef.value && !memIsGroupOwnerRef.value)

const memRoleInfoRef = computed(() => {
  switch (memDisplayRoleRef.value) {
    case GroupMemberRole.Owner:
      return "An owner can view and manage all aspects of the group."
    case GroupMemberRole.Administrator:
      return "An administrator can view and manage all aspects of the group, except changing the owner or deleting the group."
    case GroupMemberRole.Recruiter:
    case GroupMemberRole.Member:
      return "A member can see other members and any profiles and trees shared with the group."
    case GroupMemberRole.Viewer:
      return "A viewer can see the group itself, but not the group members, shared profiles or shared trees."
    default:
      return undefined
  }
})

watch(() => props.pm.member, async () => {
  if (props.pm.member) {
    ignoreChanges = true

    Object.assign(editMember, props.pm.member)

    await nextTick() // allow reactive effects to execute
    ignoreChanges = false
  }
}, { deep: true })

async function toggleShareProfile(e: Event) {
  const checkElem = e.target as HTMLInputElement
  if (checkElem.checked) {
    await memberManager.shareProfilesAsync(props.familyGroupId, [props.pm.profileGroupId!])
  }
  else {
    await memberManager.removeSharedProfilesAsync(props.familyGroupId, [props.pm.profileGroupId!])
  }
}

async function saveMemberChangesAsync(changes: PatchChange[]) {
  try {
    if (!ignoreChanges) {
      busyRef.value = true
      await memberStore.patchAsync(props.pm.member!.key!, changes)
    }
  }
  finally {
    busyRef.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!_ctx.pm.canModifyProfile && !_ctx.pm.userId)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, " This profile is managed by someone else. "))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("label", _hoisted_5, [
          _createTextVNode(" Profile shared with group "),
          (_ctx.pm.lastSharingInvite?.isPending)
            ? (_openBlock(), _createElementBlock("img", _hoisted_6))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("input", {
          type: "checkbox",
          id: "shareProfileOption",
          checked: _ctx.pm.isProfileShared,
          disabled: busyRef.value || !_ctx.pm.canModifyProfile || _ctx.pm.hasPendingInvite,
          class: "form-check-input",
          onChange: toggleShareProfile
        }, null, 40, _hoisted_7),
        (canEditGroupRef.value && !_ctx.pm.canModifyProfile)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (_ctx.pm.isProfileShared)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "btn btn-inline btn-link",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('removeProfile', _ctx.pm.profileGroupId!)))
                  }, " Remove "))
                : (!_ctx.pm.hasPendingInvite)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      type: "button",
                      class: "btn btn-inline btn-link",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('shareProfile', _ctx.pm.profileGroupId!)))
                    }, " Request "))
                  : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _hoisted_8
    ]),
    (_ctx.pm.member || _ctx.pm.hasPendingGroupInvite)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _hoisted_10,
          _createElementVNode("div", _hoisted_11, [
            _createTextVNode(_toDisplayString(memDisplayRoleRef.value) + " ", 1),
            (_ctx.pm.hasPendingGroupInvite)
              ? (_openBlock(), _createElementBlock("img", _hoisted_12))
              : (canChangeRoleRef.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    type: "button",
                    class: "btn btn-inline btn-link",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('removeUser', _ctx.pm)))
                  }, [
                    (memUserIsSelfRef.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(" Leave group ")
                        ], 64))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createTextVNode(" Remove ")
                        ], 64))
                  ]))
                : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_13, _toDisplayString(memRoleInfoRef.value), 1)
        ]))
      : _createCommentVNode("", true),
    (_ctx.pm.member && !memIsGroupOwnerRef.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createElementVNode("div", _hoisted_15, [
            _hoisted_16,
            _withDirectives(_createElementVNode("input", {
              type: "checkbox",
              id: "canInviteOption",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((memCanInviteRef).value = $event)),
              disabled: busyRef.value || !canChangeRoleRef.value,
              class: "form-check-input"
            }, null, 8, _hoisted_17), [
              [_vModelCheckbox, memCanInviteRef.value]
            ])
          ]),
          _hoisted_18
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})